import {
    LockManager,
    PulseStorage,
    PulseStorageOptions,
    SharedConfigurations,
    isLocalStorageSupported,
    isToggleOn,
} from '@pulse/common';
import { PulseEnvironment } from '../../globals';
import { Quote, TabStorage, TransientStorage } from '../storage';
import { UUIDService } from '../uuid.service';

export class TabManagerService {
    private tabIdKey = 'tab_id';
    private tabId!: string;
    enabled = true;
    private config: PulseStorageOptions = { storagePrefix: 'paa_' };

    constructor(
        private tabStorage: PulseStorage,
        private transientStorage: TransientStorage,
        private lockManager: LockManager,
        private uuid: UUIDService,
        private quote: Quote,
        private env: PulseEnvironment,
        config: SharedConfigurations,
    ) {
        this.enabled = isToggleOn(config.eventEnrichment, 'tab_id');
    }

    async init() {
        if (!this.enabled) {
            return;
        }

        this.tabId = await this.trackTabId();
    }

    getSiblingTabId(siblingRef: PulseEnvironment): string | null {
        try {
            if (!isLocalStorageSupported(this.env.win)) {
                return null;
            }

            if (!this.isSameHost(siblingRef.win, this.env.win)) {
                return null;
            }

            const storageRef = new TabStorage(
                siblingRef,
                this.config,
                this.quote,
                this.transientStorage,
            );

            return storageRef.get(this.tabIdKey);
        } catch {
            console.error('Sibling tab referance is invalid');

            return null;
        }
    }

    getTabId(): string {
        if (!this.tabId) {
            throw new Error('TabManager is not initialized!');
        }

        return this.tabId;
    }

    async trackTabId(): Promise<string> {
        const storedTabId = this.tabStorage.get(this.tabIdKey);
        let tabId: string | null = await this.lockAndUseTabId(storedTabId);

        if (!tabId) {
            const newTabId = this.uuid.generate();
            tabId = await this.lockAndUseTabId(newTabId);

            if (!tabId) {
                console.warn(`Failed to lock the tab identifier!`);
            }

            return newTabId;
        }

        return tabId;
    }

    private async lockAndUseTabId(tabId: string | null): Promise<string | null> {
        if (!tabId) {
            return null;
        }

        const lockKey = `${this.tabIdKey}_${tabId}`;
        const lock = await this.lockManager.request(lockKey);

        if (!lock) {
            this.tabStorage.clear();

            return null;
        }

        this.tabStorage.set(this.tabIdKey, tabId);

        return tabId;
    }

    private isSameHost(openerWin: Window, currentWin: Window): boolean {
        try {
            return openerWin.location.host === currentWin.location.host;
        } catch {
            // Cross-origin access will throw an error
            return false;
        }
    }
}
